import { useParams } from 'react-router-dom';

import {
  getFormattedFulfillmentDateTime,
  getFormattedFulfillmentDateTimeRange
} from 'catering/helpers/dates';
import { fulfillmentOptions } from 'constants/fulfillmentOptions';
import { useAccount } from 'catering/context/providers/AccountProvider';
import {
  selectCateringLocationAvailableFulfillmentOptions,
  selectCateringLocationBySlug,
  selectIsCartEmpty,
  useCart,
  useCateringLocations
} from 'catering/hooks/query';
import { useQueryParams } from 'catering/hooks/useQueryParams';
import { validateReferralSource } from 'bento-ordering/base/utils/validateReferralSource';
import { validateUTMCampaign } from 'bento-ordering/base/utils/validateUTMCampaign';

export function useAppBarData() {
  const { locationSlug } = useParams();
  const account = useAccount();

  const queryParams = useQueryParams();
  const { utm_source: utmSource, utm_campaign } = queryParams;
  const validReferralSource = validateReferralSource(utmSource);
  const params = {};

  if (validReferralSource) {
    params.referralSource = validReferralSource;
  }

  const validUTMCampaign = validateUTMCampaign(utm_campaign);

  if (validUTMCampaign) {
    params.utmCampaign = validUTMCampaign;
  }

  const { isLoading: isLoadingCart, data: cart } = useCart(
    {
      select: cart => ({
        ...cart,
        isEmpty: selectIsCartEmpty(cart)
      })
    },
    params
  );

  const {
    isLoading: isLoadingLocationsData,
    data: cateringLocation
  } = useCateringLocations({
    select: locations => {
      const selectedLocation = selectCateringLocationBySlug(
        locations,
        locationSlug
      );

      const availableFulfillmentOptions = selectCateringLocationAvailableFulfillmentOptions(
        selectedLocation
      );

      return {
        ...selectedLocation,
        availableFulfillmentOptions
      };
    }
  });

  if (isLoadingCart || isLoadingLocationsData) {
    return { isLoading: true };
  }

  const { availableFulfillmentOptions } = cateringLocation;

  const fulfillmentAddress =
    cart.catering_fulfillment_method === fulfillmentOptions.DELIVERY
      ? cart.shipping_street_address_1 || 'Your Address'
      : null;

  const activeFulfillmentMethod =
    cart.catering_fulfillment_method || availableFulfillmentOptions[0];

  const fulfillmentDateTime =
    cart.fulfillment_date && cart.fulfillment_time
      ? getFormattedFulfillmentDateTime(
          cart.fulfillment_date,
          cart.fulfillment_time
        )
      : null;

  const fulfillmentDateTimeRange =
    cart.fulfillment_date &&
    cart.fulfillment_time &&
    cart.catering_fulfillment_datetime_range_local
      ? getFormattedFulfillmentDateTimeRange(
          cart.fulfillment_date,
          cart.fulfillment_time,
          cart.catering_fulfillment_datetime_range_local
        )
      : null;

  return {
    data: {
      account: {
        logo: account.logo,
        logoDestinationUrl: account.logoDestinationUrl,
        name: account.name
      },
      activeFulfillmentMethod,
      availableFulfillmentOptions,
      cart: {
        quantity: cart.quantity,
        subtotal: cart.subtotal,
        isEmpty: cart.isEmpty
      },
      fulfillmentAddress,
      fulfillmentDateTime,
      fulfillmentDateTimeRange,
      locationSlug
    }
  };
}
